import { Link } from 'react-router-dom';
import { motion } from '../lib/motion';
import { MapPin, Building2, TriangleAlert, Truck } from 'lucide-react';
import { anneesExperience } from '../lib/anciennete';
import { communesDeProximite, communeUrl, RACINE_ZONES, SLUGS_PRIORITAIRES } from '../data/communes';
import CommunesPrioritaires from './CommunesPrioritaires';
import { estPubliable, PAGE_PRIX_DEMENAGEMENT, PAGE_CHANGEMENT_ADRESSE } from '../data/pages-en-preparation';

// Quartiers de Liège où l'entreprise intervient. Aucun concurrent de la première
// page ne descend à ce niveau de granularité : le premier cite des communes de
// la province, jamais des quartiers.
const QUARTIERS = [
  'Centre-ville', 'Outremeuse', 'Amercœur', 'Longdoz', 'Guillemins', 'Laveu',
  'Cointe', 'Sainte-Walburge', 'Rocourt', 'Grivegnée', 'Chênée', 'Jupille',
  'Wandre', 'Bressoux', 'Droixhe', 'Angleur', 'Sclessin', 'Fétinne',
];

// Les communes voisines ne sont plus énumérées ici.
//
// Elles l'étaient, sous forme de slugs — un progrès sur les noms écrits en dur,
// mais un progrès à mi-chemin : la LISTE restait écrite à la main, et elle a
// vieilli. Elle comptait vingt-deux entrées quand l'arrondissement en compte
// vingt-trois publiées hors Liège ; Dalhem manquait, sans que rien ne le
// signale. La même liste, tenue séparément dans llms.txt, en comptait quinze
// autres.
//
// `communesDeProximite()` est un filtre sur la table, pas une copie : publier
// ou dépublier une commune se voit ici sans qu'on y touche.

/**
 * Pages spécialisées vers lesquelles l'accueil renvoie.
 *
 * Quatre d'entre elles ne recevaient qu'un à trois liens entrants, tous depuis
 * des pages elles-mêmes faibles. L'accueil est la seule page réellement forte
 * du site : c'est de là que le lien vaut quelque chose.
 */
const LIENS_APPROFONDIS = [
  {
    // Filtré au rendu tant que la page est verrouillée.
    to: PAGE_PRIX_DEMENAGEMENT,
    label: "Prix d'un déménagement à Liège",
    desc: 'les quatre facteurs du devis, et pourquoi nous ne facturons pas au mètre cube.',
  },
  {
    // Filtré au rendu tant que le hub est verrouillé (lot 11).
    to: PAGE_CHANGEMENT_ADRESSE,
    label: "Changement d'adresse à Liège",
    desc: 'qui prévenir et dans quel délai : CILE, RESA, bpost, commune — la checklist, sources officielles datées.',
  },
  {
    to: '/demenagement/demenageur-liege',
    label: 'Déménageur à Liège',
    desc: 'nos effectifs, notre flotte et l\'organisation d\'un chantier en ville.',
  },
  {
    to: '/demenagement/demenagement-piano',
    label: 'Déménagement de piano',
    desc: 'un instrument ne se transporte pas comme un meuble.',
  },
  {
    to: '/demenagement/demontage-remontage-meubles',
    label: 'Démontage et remontage de meubles',
    desc: 'ce qui ne passe pas la porte monté, et ce qu\'on remonte à l\'arrivée.',
  },
  {
    to: '/garde-meubles/garde-meubles-liege',
    label: 'Garde-meubles à Liège',
    desc: 'nos box, leurs accès et leurs conditions.',
  },
  {
    to: '/blog/6-conseils-reussir-demenagement-liege',
    label: '6 conseils pour réussir son déménagement',
    desc: 'ce qui se prépare des semaines à l\'avance.',
  },
  {
    to: '/blog/6-erreurs-eviter-demenagement-liege',
    label: '6 erreurs à éviter',
    desc: 'les oublis qui coûtent le plus cher le jour J.',
  },
];


const CONTRAINTES = [
  {
    icon: TriangleAlert,
    titre: 'Rues étroites et pentes du centre historique',
    texte: "Le Laveu, Cointe ou les abords de la Citadelle imposent des rues en forte pente et des accès resserrés. Nos chauffeurs connaissent les gabarits praticables et nous adaptons le véhicule au quartier plutôt que l'inverse.",
  },
  {
    icon: Building2,
    titre: 'Immeubles anciens sans ascenseur',
    texte: "Une grande partie du bâti liégeois, en Outremeuse comme au Longdoz, compte trois à quatre étages sans ascenseur. Notre monte-meubles évite le portage dans les cages d'escalier et réduit fortement les risques de dégâts.",
  },
  {
    icon: MapPin,
    titre: 'Stationnement réglementé',
    texte: "La Ville de Liège exige une autorisation d'occupation de la voirie pour réserver un emplacement le jour du déménagement. Nous nous chargeons de la demande et de la pose des panneaux.",
  },
  {
    icon: Truck,
    titre: 'Accès contraints et zones piétonnes',
    texte: "Carré, rue Saint-Paul, place du Marché : certaines adresses ne sont accessibles qu'à des créneaux précis. Nous planifions l'intervention en fonction de ces fenêtres horaires.",
  },
];

export default function LiegeSection() {
  // Une commune passée en brouillon est servie en noindex : lui envoyer un lien
  // depuis la page la plus forte du site dépenserait du maillage pour rien.
  //
  // Les dix communes prioritaires sont liées en prose juste au-dessus, avec
  // une ancre propre à chacune : elles sont retirées des pastilles, pour ne
  // pas répéter le même lien sous une ancre générique.
  const communes = communesDeProximite().filter((c) => !SLUGS_PRIORITAIRES.includes(c.id));

  const ans = anneesExperience();

  return (
    <section id="demenagement-liege" className="bg-white py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-4 md:px-8">

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block bg-navy text-yellow text-[11px] uppercase tracking-[0.2em] font-bold rounded-full px-4 py-1.5 mb-4">
            NOTRE TERRITOIRE
          </span>
          {/* H2 auto-suffisant : compréhensible hors contexte, condition d'une
              bonne extraction par les moteurs de réponse. */}
          <h2 className="text-3xl md:text-4xl font-black uppercase text-navy leading-tight mb-6">
            Déménagement à Liège : {ans} ans de terrain liégeois
          </h2>

          {/* Answer Capsule — bloc de tête destiné à l'extraction par les IA.
              Contient les entités nommées : entreprise, ville, adresse, date de
              fondation, capacités, périmètre. */}
          <p className="text-navy text-lg md:text-xl leading-relaxed border-l-4 border-yellow pl-5 mb-8 font-medium">
            Déménagements Gramme est une entreprise familiale établie voie du
            Belvédère 1 à Seraing, en périphérie de Liège. Elle est active depuis
            1948, soit {ans} ans et trois générations. Nous déménageons
            particuliers et entreprises dans tous les quartiers liégeois. Nos
            véhicules vont de 4 à 100 m³ et sont équipés d'élévateurs. Le devis
            est gratuit et remis sous 24 heures ouvrables.
          </p>

          <p className="text-muted text-[17px] leading-relaxed mb-4">
            Déménager à Liège ne ressemble pas à déménager ailleurs. La ville
            cumule trois contraintes. Son bâti ancien est souvent dépourvu
            d'ascenseur. Ses rues montent, héritage d'une topographie de vallée.
            Et son hypercentre applique des règles de stationnement strictes. Un
            déménageur qui les ignore découvre le problème le matin du
            chargement, au moment où il coûte le plus cher.
          </p>
          <p className="text-muted text-[17px] leading-relaxed">
            C'est là que joue l'expérience locale accumulée depuis 1948. Nous
            savons quel gabarit de camion passe rue Hors-Château. Nous savons
            combien de temps prend un troisième étage sans ascenseur au Longdoz.
            Et nous savons quand déposer une demande d'autorisation de voirie
            auprès de la Ville pour qu'elle soit accordée à temps.
          </p>
        </motion.div>

        {/* Les 4 contraintes — chaque bloc est un passage extractible autonome */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
          {CONTRAINTES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.titre}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="bg-offwhite rounded-2xl p-6 border border-gray-100"
              >
                <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-yellow" />
                </div>
                <h3 className="text-navy font-bold text-lg mb-2">{c.titre}</h3>
                <p className="text-muted text-[15px] leading-relaxed">{c.texte}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Q&A inline — question en gras puis réponse directe, format que les
            modèles de langage extraient volontiers. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          className="mt-12 bg-navy/5 rounded-2xl p-6 md:p-8 border border-navy/10"
        >
          <p className="text-navy font-bold text-lg mb-2">
            Combien de temps faut-il prévoir pour un déménagement à Liège ?
          </p>
          <p className="text-muted text-[16px] leading-relaxed mb-6">
            Comptez une demi-journée à une journée pour un appartement de deux à
            trois pièces, et une journée complète pour une maison familiale.
            Trois éléments allongent cette durée : un étage élevé sans ascenseur,
            une rue en pente, un accès en zone piétonne. Ils pèsent souvent plus
            que le volume lui-même.
          </p>

          <p className="text-navy font-bold text-lg mb-2">
            Faut-il une autorisation pour stationner un camion de déménagement à Liège ?
          </p>
          <p className="text-muted text-[16px] leading-relaxed">
            Oui, la Ville de Liège délivre une autorisation d'occupation de la
            voirie qui permet de réserver un emplacement devant l'adresse. La
            demande doit être introduite plusieurs jours à l'avance. Nous nous en
            occupons pour vous, panneaux de signalisation compris.
          </p>
        </motion.div>

        {/* Quartiers — l'actif géographique le plus fin du site */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          className="mt-12"
        >
          <h3 className="text-2xl font-black uppercase text-navy mb-3">
            Les quartiers de Liège où nous intervenons
          </h3>
          <p className="text-muted text-[16px] leading-relaxed mb-5">
            Nous couvrons l'ensemble du territoire communal liégeois, de la rive
            gauche aux hauteurs de Cointe, en passant par les quartiers de la
            rive droite :
          </p>
          <div className="flex flex-wrap gap-2">
            {QUARTIERS.map((q) => (
              <span key={q} className="bg-offwhite border border-gray-200 text-navy text-sm rounded-full px-3.5 py-1.5">
                {q}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Communes — zone de chalandise élargie */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          className="mt-10"
        >
          <h3 className="text-2xl font-black uppercase text-navy mb-3">
            Les communes de la province que nous desservons
          </h3>
          {/* LOT 3 du plan SEO : les dix communes prioritaires en prose, avec
              une ancre différente pour chacune, avant la liste de pastilles.
              Un lecteur y trouve ce qui distingue chaque commune ; un moteur
              y trouve dix liens de corps de page depuis la page la plus forte
              du site, là où il ne voyait qu'une liste. */}
          <p className="text-muted text-[17px] leading-relaxed mb-4">
            Dix communes concentrent l'essentiel de nos chantiers. Chacune a sa
            page, avec la distance depuis le dépôt et la procédure de
            stationnement relevée à la source officielle.
          </p>
          <CommunesPrioritaires variante="accueil" className="mb-8" />
          <p className="text-muted text-[16px] leading-relaxed mb-5">
            Dans le reste de l'arrondissement de Liège, notre dépôt nous place
            à quelques minutes de chaque commune, et chacune a également sa
            page :
          </p>
          <div className="flex flex-wrap gap-2">
            {communes.map((c) => (
              <Link
                key={c.id}
                to={communeUrl(c)}
                className="bg-navy text-white text-sm rounded-full px-3.5 py-1.5 hover:bg-navy/80 transition-colors"
              >
                Déménagement à {c.nom}
              </Link>
            ))}
          </div>
          <p className="text-muted text-[15px] leading-relaxed mt-5">
            Votre commune n'apparaît pas dans cette liste&nbsp;? Nous couvrons
            l'ensemble de la province de Liège, commune par commune, sur notre{' '}
            <Link to={RACINE_ZONES} className="text-navy font-bold underline hover:text-navy/70 transition-colors">
              page des zones d'intervention
            </Link>
            , et toute la Belgique. La France, la
            Suisse, l'Espagne et l'Italie relèvent de nos{' '}
            <Link to="/demenagement/demenagement-international" className="text-navy font-bold underline hover:text-navy/70 transition-colors">
              déménagements internationaux depuis Liège
            </Link>
            . Appelez-nous, nous vous dirons en deux minutes si nous pouvons
            intervenir.
          </p>

          {/* Renvois vers les pages spécialisées.
              L'accueil est la page la plus forte du site, et elle ne renvoyait
              vers aucune d'elles : /demenagement/demenageur-liege ne recevait
              qu'un seul lien entrant, depuis une page qui en a deux.
              Placé en fin de section « territoire » plutôt qu'en pied de page :
              un lecteur arrivé jusqu'ici cherche du détail, et l'ancre porte le
              sujet de la page visée. */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-black uppercase text-navy mb-4">
              Pour aller plus loin
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {LIENS_APPROFONDIS.filter((l) => estPubliable(l.to)).map((l) => (
                <li key={l.to} className="text-muted text-[15px] leading-relaxed">
                  <Link to={l.to} className="text-navy font-bold underline hover:text-navy/70 transition-colors">
                    {l.label}
                  </Link>
                  {' — '}{l.desc}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
